import { Component, DestroyRef, inject, OnInit, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
  FormArray,
  FormControl,
  FormGroup,
  ReactiveFormsModule,
  ValidationErrors,
} from '@angular/forms';
import { SecurityContext } from '@angular/core';
import { DomSanitizer } from '@angular/platform-browser';
import { ActivatedRoute, Router } from '@angular/router';
import { AuthService } from '@aggregator/core';
import { FormBuilderService, IFormGroupConfig, UIButton, UIFormModule } from '@shared/ui';
import { catchError, of } from 'rxjs';
import { confirmPasswordFormConfig } from './set-password.form';

@Component({
  selector: 'aggregator-set-password-page',
  standalone: true,
  imports: [UIButton, UIFormModule, ReactiveFormsModule],
  templateUrl: './set-password.component.html',
  styleUrl: './set-password.component.scss',
})
export class SetPasswordPage implements OnInit {
  isFormSubmitting = false;

  form?: FormGroup;
  formConfig: IFormGroupConfig = confirmPasswordFormConfig;

  private formBuilder = inject(FormBuilderService);
  private router = inject(Router);
  private authService = inject(AuthService);
  private destroy$ = inject(DestroyRef);
  private sanitizer = inject(DomSanitizer);
  private activatedRoute = inject(ActivatedRoute);

  tempToken = signal<string | null>(null);

  get isFormInvalid(): boolean | undefined {
    return this.form?.invalid;
  }

  ngOnInit(): void {
    this.form = this.formBuilder.build(this.formConfig);
    this.tempToken.set(this.activatedRoute.snapshot.queryParamMap.get('token'));
  }

  setErrorsRecursively(form: FormGroup | FormArray, error: ValidationErrors): void {
    Object.keys(form.controls).forEach(key => {
      const control = form.get(key);

      if (control instanceof FormControl) {
        control.setErrors(error);
      } else if (control instanceof FormGroup || control instanceof FormArray) {
        this.setErrorsRecursively(control, error);
      }
    });
  }

  onSubmit(): void {
    if (this.isFormSubmitting || this.isFormInvalid) {
      return;
    }

    this.form?.disable();

    const payload = {
      tempToken: this.tempToken(),
      ...this.form?.getRawValue(),
    };

    this.authService
      .onResetPassword(payload)
      .pipe(
        takeUntilDestroyed(this.destroy$),
        catchError(() => {
          this.isFormSubmitting = false;
          this.form?.enable();
          return of(false);
        }),
      )
      .subscribe(isAuth => {
        if (isAuth) {
          this.router.navigate(['auth']);
          return;
        }
        this.isFormSubmitting = false;
        this.form?.enable();
        throw new Error('Неверный логин или пароль!');
      });
  }

  onLinked(fieldName: string): void {
    const urls: Record<string, string> = {
      lawPersonalData: 'https://adilet.zan.kz/rus/docs/Z1300000094',
      lawInformatization: 'https://adilet.zan.kz/rus/docs/Z1500000418',
    };

    const safeUrl = this.sanitizer.sanitize(SecurityContext.URL, urls[fieldName]);
    if (safeUrl) {
      window.open(safeUrl, '_blank');
    }
  }
}
